// UITwoColumnWrapper.tsx
import React from 'react';
import './UITwoColumnWrapper.scss';

export interface UITwoColumnWrapperProps {
  /**
   * Два дочірні елементи для колонок
   */
  children: [React.ReactNode, React.ReactNode];

  /**
   * Варіант spacing між колонками
   */
  gap?: 'small' | 'medium' | 'large';

  /**
   * Варіант padding контейнера
   */
  spacing?: 'tight' | 'compact' | 'default' | 'spacious';

  /**
   * Вирівнювання колонок по висоті
   */
  equalHeight?: boolean;

  /**
   * Layout на мобільних пристроях
   */
  mobileLayout?: 'stack' | 'keep';

  /**
   * Співвідношення ширини колонок (лівої до правої)
   */
  ratio?: '1:1' | '1:2' | '2:1' | '1:3' | '3:1';

  /**
   * Варіант тіні
   */
  shadow?: 'none' | 'default' | 'light' | 'medium' | 'strong';

  /**
   * Варіант фону
   */
  background?: 'white' | 'transparent' | 'light' | 'gray';

  /**
   * Варіант рамки
   */
  border?: 'none' | 'default' | 'primary';

  /**
   * Інтерактивні ефекти
   */
  interactive?: 'none' | 'hoverable' | 'clickable';

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку (тільки для clickable варіанта)
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * HTML атрибути
   */
  [key: string]: any;
}

// Моковые данные для демонстрації
const mockProps: Partial<UITwoColumnWrapperProps> = {
  gap: 'medium',
  spacing: 'default',
  equalHeight: false,
  mobileLayout: 'stack',
  ratio: '1:1',
  shadow: 'none',
  background: 'transparent',
  border: 'none',
  interactive: 'none',
};

const baseClass = 'UITwoColumnWrapper';

const UITwoColumnWrapper: React.FC<UITwoColumnWrapperProps> = ({
                                                                 children,
                                                                 gap = mockProps.gap,
                                                                 spacing = mockProps.spacing,
                                                                 equalHeight = mockProps.equalHeight,
                                                                 mobileLayout = mockProps.mobileLayout,
                                                                 ratio = mockProps.ratio,
                                                                 shadow = mockProps.shadow,
                                                                 background = mockProps.background,
                                                                 border = mockProps.border,
                                                                 interactive = mockProps.interactive,
                                                                 className = '',
                                                                 onClick,
                                                                 ...props
                                                               }) => {
  // Перевіряємо що передано рівно 2 елементи
  if (!Array.isArray(children) || children.length !== 2) {
    console.warn('UITwoColumnWrapper: Expected exactly 2 children');
    return null;
  }

  const containerClasses = [
    baseClass,
    // Gap
    gap !== 'medium' && `${baseClass}--gap-${gap}`,
    // Spacing
    spacing !== 'default' && `${baseClass}--${spacing}`,
    // Equal height
    equalHeight && `${baseClass}--equal-height`,
    // Mobile layout
    mobileLayout !== 'stack' && `${baseClass}--mobile-${mobileLayout}`,
    // Ratio
    ratio && ratio !== '1:1' && `${baseClass}--ratio-${ratio.replace(':', '-')}`,
    // Shadow
    shadow === 'default' && `${baseClass}--default-shadow`,
    shadow === 'light' && `${baseClass}--light-shadow`,
    shadow === 'medium' && `${baseClass}--medium-shadow`,
    shadow === 'strong' && `${baseClass}--strong-shadow`,
    // Background
    background === 'white' && `${baseClass}--white`,
    background === 'light' && `${baseClass}--light`,
    background === 'gray' && `${baseClass}--gray`,
    // Border
    border === 'default' && `${baseClass}--bordered`,
    border === 'primary' && `${baseClass}--primary-border`,
    // Interactive
    interactive === 'hoverable' && `${baseClass}--hoverable`,
    interactive === 'clickable' && `${baseClass}--clickable`,
    // Custom
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (interactive === 'clickable' && onClick) {
      onClick(event);
    }
  };

  return (
    <div
      className={containerClasses}
      onClick={handleClick}
      role={interactive === 'clickable' ? 'button' : undefined}
      tabIndex={interactive === 'clickable' ? 0 : undefined}
      {...props}
    >
      <div className={`${baseClass}__columns`}>
        <div className={`${baseClass}__column ${baseClass}__column--left`}>
          {children[0]}
        </div>
        <div className={`${baseClass}__column ${baseClass}__column--right`}>
          {children[1]}
        </div>
      </div>
    </div>
  );
};

export default UITwoColumnWrapper;